import { Align, Bar, Divider, List, Position, Row, Size, Tooltip } from '@busybusy/webapp-react-ui';
import { useQuery } from '@tanstack/react-query';
import {
  GetProjectWithSubProjectsQuery,
  GetProjectWithSubProjectsQueryVariables,
  Project,
  ProjectPickerDetailsQuery,
  ProjectPickerDetailsQueryVariables,
  ProjectPickerFlatQuery,
  ProjectPickerFlatQueryVariables,
  ProjectSearchFields,
  SearchType,
  type CostCode,
  type ProjectWithChildrenAncestorsQuery,
  type ProjectWithChildrenAncestorsQueryVariables,
} from '__generated__/graphql';
import classNames from 'classnames';
import PickerList from 'components/foundation/pickers/PickerList/PickerList';
import PickerListPicker from 'components/foundation/pickers/PickerListPicker/PickerListPicker';
import PickerListSearchHeader from 'components/foundation/pickers/PickerListSearchHeader/PickerListSearchHeader';
import { useDebounce } from 'hooks';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useGraphQLPaging from 'hooks/graphql/useGraphQLPaging/useGraphQLPaging';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { useReactQueryLazyLoading } from 'hooks/react-query/useReactQueryLazyLoading/useReactQueryLazyLoading';
import { t } from 'i18next';
import _, { cloneDeep, first, isEmpty, isNil, keyBy, sortBy } from 'lodash';
import * as React from 'react';
import { ReactNode } from 'react';
import { ClassName } from 'types/ClassName';
import { NonNullArrayField } from 'types/util/NonNullArrayField';
import { Optional } from 'types/util/Optional';
import { getGraphQlContainsComparison, getGraphQlDoesNotContainComparison } from 'utils/apolloUtils';
import { convertEmptyToNull, highlight } from 'utils/stringUtils';
import { logError } from 'utils/testUtils';
import { IconButton } from '../../..';
import { ArrowBackIcon, ChevronRightIcon } from '../../../../assets/icons';
import { projectUtils } from '../../../../utils';
import { projectFlatQuery, projectPickerDetails, projectQuery, subProjectQuery } from './project-picker-queries';
import './ProjectPicker.scss';

export interface IProjectPickerProps {
  value: string | null;
  error?: boolean;
  onSelect: (projectId: string | null) => void;
  placeholder?: string;
  className?: ClassName;
  position?: Position;
  client?: any;
  dropDownButtonRender?: ReactNode; // Drop down button on the picker
  closeButtonRender?: (handleClear: (event: React.KeyboardEvent | React.MouseEvent) => void) => ReactNode; // Close button render on picker
  fetchPolicy?: string;
  searchArchivedProjects?: boolean;
  blacklistedIds?: string[];
  topLevelOnly?: boolean;
  scopeToCostCodeIds?: Array<CostCode['id']>;
  disabled?: boolean;
}

type ProjectPickerProject = NonNullArrayField<ProjectPickerDetailsQuery['projects']>;

type ProjectPickerDisplayProject = {
  id: string;
  title: string;
  archivedOn?: string | null;
  ancestors?: Array<{ id: string; title: string }> | null;
  children?: Array<{ id: string; archivedOn?: string | null }> | null;
};

export const PROJECT_PICKER_SECTION_SIZE = 500;

function ProjectPicker({
  value,
  error,
  onSelect,
  placeholder,
  className,
  position,
  dropDownButtonRender,
  closeButtonRender,
  searchArchivedProjects,
  blacklistedIds,
  topLevelOnly,
  scopeToCostCodeIds,
  disabled,
}: IProjectPickerProps) {
  const [searchValue, setSearchValue] = React.useState('');
  const [layers, setLayers] = React.useState<Array<Omit<ProjectPickerDisplayProject, 'cursor'>>>([]);
  const [focusStartId, setFocusStartId] = React.useState<string | null>(null);

  const graphqlClient = useGraphQLClient();
  const graphqlPaging = useGraphQLPaging();
  const baseQueryKey = useReactQueryBaseKey();

  const projectPageSize = 10;
  const tooltipDelay = 750;
  const dividerIndex = React.useRef<number | null>(null);
  const pickerRef = React.useRef<HTMLElement | null>(null);
  const searchDebounce = useDebounce(searchValue, 250);

  const [isOpen, setIsOpen] = React.useState(false);

  const { data: selectedProject } = useQuery({
    queryKey: [...baseQueryKey, 'project-picker-by-id', value],
    queryFn: async () => {
      if (!value) {
        return null;
      }

      const project = _.find(data, (model) => model.id === value);

      if (project) {
        return project;
      } else {
        const projectResult = await graphqlClient.request<
          ProjectWithChildrenAncestorsQuery,
          ProjectWithChildrenAncestorsQueryVariables
        >({
          document: projectQuery,
          variables: {
            filter: { id: { equal: value } },
          },
        });

        const targetProject = first(projectResult?.projects) ?? null;
        setLayers(_.sortBy(targetProject?.ancestors, 'depth'));

        return targetProject;
      }
    },
    initialData: null,
    enabled: !!value,
  });

  const isDrilledIn = layers.length > 0;
  const hasSearch = !!searchDebounce && searchDebounce.length > 0;

  const variables: ProjectWithChildrenAncestorsQueryVariables = React.useMemo(
    () => ({
      filter: {
        id: getGraphQlDoesNotContainComparison(blacklistedIds),
        archivedOn: searchArchivedProjects ? { isNull: false } : { isNull: true },
        parentProjectId: topLevelOnly || !hasSearch ? { isNull: true } : undefined,
        projectCostCodeLink: scopeToCostCodeIds
          ? { costCodeId: getGraphQlContainsComparison(scopeToCostCodeIds) }
          : undefined,
        search:
          hasSearch && !isDrilledIn
            ? {
                type: SearchType.Contains,
                value: searchDebounce,
                fields: [ProjectSearchFields.Title, ProjectSearchFields.Number],
              }
            : undefined,
      },
      // NOTE: There's a weird issue with sorting when searching on graphql performance wise, during search we're auto paging and then sorting manually
      sort: !hasSearch ? [{ title: 'asc' }] : undefined,
    }),
    [blacklistedIds, hasSearch, isDrilledIn, scopeToCostCodeIds, searchArchivedProjects, searchDebounce, topLevelOnly]
  );

  const scroller = React.useRef<HTMLElement>();

  const {
    data: nonSearchData,
    isLoading: nonSearchLoading,
    isError: nonSearchError,
    loadedAll,
  } = useReactQueryLazyLoading(
    scroller.current,
    [...baseQueryKey, 'project-picker', variables],
    loadProjects,
    projectPageSize,
    { enabled: isOpen && !isDrilledIn && !hasSearch, refetchOnWindowFocus: false }
  );

  const {
    data: projectSearchData,
    isLoading: isProjectSearchLoading,
    isError: isProjectSearchError,
  } = useQuery({
    queryKey: [...baseQueryKey, 'project-picker-search', variables],
    queryFn: () => loadProjects(),
    enabled: isOpen && hasSearch && !isDrilledIn,
    refetchOnWindowFocus: false,
  });

  const data = hasSearch ? projectSearchData : nonSearchData;
  const isLoading = hasSearch ? isProjectSearchLoading : nonSearchLoading;
  const isDataError = hasSearch ? isProjectSearchError : nonSearchError;

  const {
    data: subProjectDataFromQuery,
    isLoading: isSubProjectLoading,
    isError: isSubProjectError,
  } = useQuery({
    queryKey: [...baseQueryKey, 'project-picker-sub-projects', layers[layers.length - 1]?.id],
    queryFn: () => loadSubProjectSheet(),
    enabled: isOpen && isDrilledIn,
    refetchOnWindowFocus: false,
  });

  async function loadProjects(after?: Optional<string>): Promise<ProjectPickerProject[]> {
    try {
      const projects = await (searchDebounce ? getProjectsWithSearch(after) : getProjectsWithoutSearch(after));

      dividerIndex.current = searchDebounce ? calculateDividerIndex(projects) : null;

      return projects;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  const getProjectsWithSearch = React.useCallback(
    async (after?: Optional<string>) => {
      const searchedProjects = await graphqlPaging<ProjectPickerFlatQuery, ProjectPickerFlatQueryVariables>(
        {
          document: projectFlatQuery,
          variables: { after, ...variables },
        },
        'projects',
        500
      );

      const projectIds = searchedProjects.map((project) => project.id);

      if (isEmpty(projectIds)) {
        return [];
      }

      const projectDetails = await graphqlPaging<ProjectPickerDetailsQuery, ProjectPickerDetailsQueryVariables>(
        {
          document: projectPickerDetails,
          variables: {
            filter: { id: getGraphQlContainsComparison(projectIds) },
          },
        },
        'projects',
        500
      );

      return sortBy(projectDetails, ({ depth, title }) => [depth !== 1, title?.toLocaleLowerCase()]);
    },
    [graphqlPaging, variables]
  );

  const getProjectsWithoutSearch = React.useCallback(
    async (after?: Optional<string>): Promise<ProjectPickerProject[]> => {
      const projectResult = await graphqlClient.request<ProjectPickerFlatQuery, ProjectPickerFlatQueryVariables>({
        document: projectFlatQuery,
        variables: { after, first: projectPageSize, ...variables },
      });

      const groupedProjects = keyBy(projectResult.projects, 'id');
      const projectIds = Object.keys(groupedProjects);

      if (isEmpty(projectIds)) {
        return [];
      }

      const projectDetailsRequest = await graphqlClient.request<
        ProjectPickerDetailsQuery,
        ProjectPickerDetailsQueryVariables
      >({
        document: projectPickerDetails,
        variables: {
          first: projectPageSize,
          filter: { id: getGraphQlContainsComparison(projectIds) },
        },
      });

      const withPreviousCursor = projectDetailsRequest.projects?.map((project) => {
        return { ...project, cursor: groupedProjects[project.id]?.cursor };
      });

      return sortBy(withPreviousCursor, ({ title }) => title?.toLocaleLowerCase());
    },
    [graphqlClient, variables]
  );

  const calculateDividerIndex = (projects: Array<Pick<Project, 'depth'>>) => {
    const firstSubprojectIndex = projects.findIndex((project) => project.depth !== 1);
    return firstSubprojectIndex ? firstSubprojectIndex - 1 : null;
  };

  async function loadSubProjectSheet(after?: Optional<string>): Promise<ProjectPickerDisplayProject[]> {
    try {
      const projects = await graphqlPaging<GetProjectWithSubProjectsQuery, GetProjectWithSubProjectsQueryVariables>(
        {
          document: subProjectQuery,
          variables: {
            after,
            first: projectPageSize,
            projectId: layers[layers.length - 1].id,
          },
        },
        'projects'
      );

      return sortBy(projects[0].children ?? [], (item) => item.title?.toLocaleLowerCase()).filter(
        (item) => item.archivedOn === null
      );
    } catch (e) {
      logError(e);
      throw e;
    }
  }

  const hasActiveSubProjects = (row: Pick<ProjectPickerProject, 'children'>) => {
    return (
      row.children &&
      row.children.length > 0 &&
      !topLevelOnly &&
      row.children.some((item) => {
        return item.archivedOn === null;
      })
    );
  };

  const renderBasicRow = (row: ProjectPickerProject, index: number) => {
    return (
      <>
        <Bar size={Size.SMALL} className="px-3">
          <div className="picker-row-title ellipsis">
            {highlight(row.title, searchValue)}
            {row.projectInfo?.number &&
              !isNil(convertEmptyToNull(searchValue)) &&
              row.projectInfo.number.toLowerCase().includes(searchValue) && (
                <>
                  <br />
                  <div className="f-sm fc-2 ellipsis">
                    <b>{t('Proj #: ')}</b>
                    {highlight(row.projectInfo.number, searchValue)}
                  </div>
                </>
              )}
          </div>
          {hasActiveSubProjects(row) && renderSubprojectIcon(row)}
        </Bar>
        {layers.length === 0 && dividerIndex.current === index && <Divider className="my-3" />}
      </>
    );
  };

  const renderSubSearchRow = (project: ProjectPickerProject) => {
    return (
      <Bar key={project.id} className="px-3 subproject-search-row" size={Size.LARGE}>
        <div>
          <div className="picker-row-title ellipsis">
            {highlight(project.title, searchValue)}
            {project.projectInfo?.number &&
              !isNil(convertEmptyToNull(searchValue)) &&
              project.projectInfo.number.toLowerCase().includes(searchValue) && (
                <>
                  <br />
                  <div className="f-sm fc-2 ellipsis">
                    <b>{t('Proj #: ')}</b>
                    {highlight(project.projectInfo.number, searchValue)}
                  </div>
                </>
              )}
          </div>
          <div className="f-sm fc-2 ellipsis">{projectUtils.getFormattedPathFromProject(project)}</div>
        </div>
        {hasActiveSubProjects(project) && renderSubprojectIcon(project)}
      </Bar>
    );
  };

  function renderSubprojectRow(row: ProjectPickerDisplayProject) {
    return (
      <Bar key={row.id} className="px-3 subproject-row" size={Size.SMALL}>
        <div className="picker-row-title ellipsis">{highlight(row.title, searchValue)}</div>
        {hasActiveSubProjects(row) && renderSubprojectIcon(row)}
      </Bar>
    );
  }

  function renderSubprojectIcon(project: ProjectPickerDisplayProject) {
    const handleChevronClick = (e: React.MouseEvent) => {
      handleChevron(project, e);
    };

    return (
      <div className="subproject-btn">
        <IconButton svg={ChevronRightIcon} ripple={false} onClick={handleChevronClick} />
      </div>
    );
  }

  function renderParentRow(row: ProjectPickerProject, index: number) {
    if (
      searchValue &&
      row.ancestors &&
      row.ancestors.length &&
      !layers.length // only show on searchable list
    ) {
      return renderSubSearchRow(row);
    } else {
      return renderBasicRow(row, index);
    }
  }

  function drillIn(project: ProjectPickerDisplayProject, _e: React.KeyboardEvent | React.MouseEvent) {
    if (project && project.children && project.children.length) {
      setFocusStartId(null);
      setLayers((prev) => [...prev, project]);
    }
  }

  function goBack() {
    const newLayers = cloneDeep(layers);
    const pop = newLayers.pop();

    if (pop) {
      setTimeout(() => {
        setFocusStartId(pop.id);
        setLayers(newLayers);
      });
    }
  }

  function handleClear() {
    setFocusStartId(null);
    setLayers([]);
    setSearchValue('');
  }

  function handleOnOpen() {
    setIsOpen(true);
    setLayers(_.sortBy(selectedProject?.ancestors ?? [], 'depth'));
    setFocusStartId(selectedProject?.id ?? null);
  }

  function onClose() {
    setIsOpen(false);
    handleClear();
  }

  function handleSelect(project: ProjectPickerProject | null) {
    onSelect(project ? project.id : null);

    setTimeout(() => {
      if (pickerRef.current) {
        pickerRef.current.focus();
      }
    });
  }

  function handleChevron(project: ProjectPickerDisplayProject, e: React.KeyboardEvent | React.MouseEvent) {
    e.stopPropagation();
    drillIn(project, e);
  }

  function buildValueTemplate() {
    if (selectedProject) {
      const label = projectUtils.getFormattedPathFromProject(selectedProject, true);

      if (label) {
        return (
          <Tooltip label={label} delay={tooltipDelay}>
            <span>{label}</span>
          </Tooltip>
        );
      }
    }
    return <></>;
  }

  function renderSecondaryHeader() {
    return (
      <Row className="picker-header px-3" align={Align.CENTER}>
        <IconButton svg={ArrowBackIcon} size={Size.SMALL} onClick={goBack} className="mr-2" />
        <div>
          <h5 className="picker-title fw-bold">{layers[layers.length - 1].title}</h5>
          <div className="f-sm fc-2">
            {layers.map((layer, index) => (
              <React.Fragment key={index}>
                {index !== layers.length - 1 && (
                  <span>
                    {layer.title} {index !== layers.length - 2 && '/'}{' '}
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </Row>
    );
  }

  function renderPickerLists(closeMenu: (shouldFocus: boolean) => void) {
    return (
      <>
        {!isDrilledIn ? (
          <PickerList
            key="project-list"
            value={focusStartId ?? value}
            onSelect={(project) => onSelect(project ? project.id : null)}
            closeMenu={closeMenu}
            isLoading={isLoading}
            data={data ?? []}
            error={isDataError}
            loadedAll={loadedAll}
            renderRow={renderParentRow}
            setScrollerRef={(ref) => (scroller.current = ref)}
            onRightKeyDown={drillIn}
            onLeftKeyDown={goBack}
            header={(onKeyDown, setFocusIndex) => (
              <PickerListSearchHeader
                className="p-3"
                searchValue={searchValue}
                autofocus={true}
                onSearch={setSearchValue}
                onKeyDown={onKeyDown}
                setFocusIndex={setFocusIndex}
              />
            )}
          >
            {(wrappedRowRender) => <List>{data?.map(wrappedRowRender)}</List>}
          </PickerList>
        ) : (
          <PickerList
            // NOTE: Key so that react knows this component is different than the one above
            // for focusing purposes
            key="subproject-list"
            value={focusStartId ?? null}
            onSelect={(project) => onSelect(project ? project.id : null)}
            closeMenu={closeMenu}
            isLoading={isSubProjectLoading}
            data={subProjectDataFromQuery ?? []}
            error={isSubProjectError}
            loadedAll={loadedAll}
            renderRow={renderSubprojectRow}
            setScrollerRef={(ref) => (scroller.current = ref)}
            onRightKeyDown={drillIn}
            onLeftKeyDown={goBack}
            header={renderSecondaryHeader}
            autofocus={true}
          >
            {(wrappedRowRender) => <List>{subProjectDataFromQuery?.map(wrappedRowRender)}</List>}
          </PickerList>
        )}
      </>
    );
  }

  return (
    <PickerListPicker
      value={value}
      onSelect={handleSelect}
      onClear={handleClear}
      className={classNames('project-picker', className)}
      error={error}
      dropDownButtonRender={dropDownButtonRender}
      closeButtonRender={closeButtonRender}
      position={position}
      placeholder={placeholder}
      valueTemplate={buildValueTemplate()}
      onOpen={handleOnOpen}
      onClose={onClose}
      minWidth="350px"
      disabled={disabled}
    >
      {renderPickerLists}
    </PickerListPicker>
  );
}

// TODO: Add archived filtering
// TODO: Implement lazy loading once server-side paging is available

export default ProjectPicker;
